import { useQuery } from '@tanstack/react-query';
import supabaseClient from '@/api/supabaseClient.ts';
import { Database } from '@/_constants/database.types';

type PaperAttempt = Database['public']['Tables']['past_paper_attempts']['Row'];
type PastPaper = Database['public']['Tables']['past_papers']['Row'];
type AttemptAnswer = Database['public']['Tables']['past_paper_attempt_answers']['Row'];
type Question = Database['public']['Tables']['questions']['Row'];

export interface AttemptWithPaper extends PaperAttempt {
  past_papers: PastPaper;
  past_paper_attempt_answers: (AttemptAnswer & {
    questions: Pick<Question, 'marks' | 'category'>;
  })[];
}

export interface AttemptWithDetails extends AttemptWithPaper {
  totalPossibleMarks: number;
  gradeOutOf9: number;
}

const useAllUserPaperAttempts = (authId: string | undefined) => {
  const queryFn = async (): Promise<AttemptWithDetails[]> => {
    if (!authId) return [];

    // Fetch all attempts with nested past_paper_attempt_answers and questions in a single query
    const { data: attempts, error } = await supabaseClient
      .from('past_paper_attempts')
      .select(
        `
        *,
        past_papers (*),
        past_paper_attempt_answers (
          *,
          questions (
            marks,
            category
          )
        )
      `
      )
      .eq('auth_id', authId)
      .eq('status', 'graded') // Only get completed attempts
      .order('submitted_at', { ascending: false });

    if (error) throw error;
    if (!attempts || attempts.length === 0) return [];

    // Calculate grade for each attempt
    const attemptsWithDetails = attempts.map((attempt) => {
      // Calculate total possible marks, counting only ONE Section B question
      const sectionAAnswers = (attempt.past_paper_attempt_answers || []).filter(
        (answer: AttemptWithPaper['past_paper_attempt_answers'][0]) => answer.questions.category !== 'SectionB'
      );
      const sectionBAnswers = (attempt.past_paper_attempt_answers || []).filter(
        (answer: AttemptWithPaper['past_paper_attempt_answers'][0]) => answer.questions.category === 'SectionB'
      );

      // Sum Section A marks
      const sectionAMarks = sectionAAnswers.reduce((sum: number, answer: AttemptWithPaper['past_paper_attempt_answers'][0]) => {
        return sum + (answer.questions.marks || 0);
      }, 0);

      // Take only ONE Section B question (the one that was graded)
      const gradedSectionB = sectionBAnswers.find((answer: AttemptWithPaper['past_paper_attempt_answers'][0]) => answer.graded_at) || sectionBAnswers[0];
      const sectionBMarks = gradedSectionB ? gradedSectionB.questions.marks || 0 : 0;

      const totalMarks = sectionAMarks + sectionBMarks || 64; // Default to 64 if no answers
      const totalPossibleMarks = totalMarks;
      const finalGrade = attempt.final_grade || 0;

      // Calculate grade out of 9
      const percentage = totalPossibleMarks > 0 ? finalGrade / totalPossibleMarks : 0;
      let gradeOutOf9 = 0;

      if (percentage >= 0.9) gradeOutOf9 = 9;
      else if (percentage >= 0.8) gradeOutOf9 = 8;
      else if (percentage >= 0.7) gradeOutOf9 = 7;
      else if (percentage >= 0.6) gradeOutOf9 = 6;
      else if (percentage >= 0.5) gradeOutOf9 = 5;
      else if (percentage >= 0.4) gradeOutOf9 = 4;
      else if (percentage >= 0.3) gradeOutOf9 = 3;
      else if (percentage >= 0.2) gradeOutOf9 = 2;
      else if (percentage >= 0.1) gradeOutOf9 = 1;

      console.log(
        `Attempt ${attempt.id}: finalGrade=${finalGrade}, totalMarks=${totalPossibleMarks}, percentage=${percentage.toFixed(3)}, gradeOutOf9=${gradeOutOf9}`
      );

      return {
        ...attempt,
        totalPossibleMarks,
        gradeOutOf9,
      } as AttemptWithDetails;
    });

    return attemptsWithDetails;
  };

  return useQuery({
    queryKey: ['all_user_past_paper_attempts', authId],
    queryFn,
    enabled: !!authId,
  });
};

export default useAllUserPaperAttempts;
